import type { AddressBookItemSummary } from "~/shared/schemas/address-book-schema.ts";
import type { Country } from "~/shared/schemas/location-state-schema.ts";

import { normalizeCountryCode } from "./quote-location-utils.ts";

/**
 * Resolves the `quoteState.{type}Country` (`Country`) that corresponds to a saved
 * address book item's `country` code, using the loaded countries list.
 *
 * Applying a saved address must keep `quoteState.{type}Country` in sync: several
 * consumers prefer the country from there (holiday filtering, the re-rate
 * fingerprint, the rate request body). Without this sync a cross-country
 * address leaves the previous country winning.
 *
 * Handles abbreviation and naming mismatches between legacy address-book values
 * (for example, `US`, `CA`, `MX`, `United States`) and the countries list
 * (`USA`, `CAN`, `MEX`). Returns `undefined` when no match is found so the caller
 * can leave the existing country untouched.
 */
export const resolveAddressBookCountry = (
	address: Pick<AddressBookItemSummary, "country">,
	countries: Country[],
): Country | undefined => {
	const countryCode = normalizeCountryCode(address.country)?.toUpperCase();
	return countries.find((c) => c.abbreviation.toUpperCase() === countryCode);
};
